/**
 * AnalyticsStudentDetailLive — per-student deep-dive wired to
 * /api/analytics/student/...
 *
 * Layout:
 *   1. M9 At-a-Glance bullets (gradient banner with tone-of-voice tags)
 *   2. M5 Reading Time — stacked bar chart (recharts), gradient bars
 *   3. M6 Confidence Crash Map — interactive 2×2 matrix; click a cell to
 *      expand a drawer showing the concepts that compose that quadrant
 *   4. M7 Common Question Themes — colored review-status chips
 *   5. M8 At Risk of Forgetting — overdue concept rows
 *
 * Mock equivalent: pages/AnalyticsStudentDetail.tsx (kept untouched for demo).
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  ScaleIcon,
  ClockIcon,
  SparklesIcon,
  ArrowLeftIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  CheckBadgeIcon,
  ShieldExclamationIcon,
  QuestionMarkCircleIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useTheme } from '../contexts/ThemeContext';
import { useAnalyticsStudent } from '../hooks/useAnalyticsLive';
import type {
  StudentTheme, OverconfidenceDangerConcept, AtRiskConceptItem, AiSummaryBullet,
  CalibrationActual, CalibrationEvent,
} from '../types/analyticsLive';

// ─── Reusable shells ───────────────────────────────────────────────────────

const Section: React.FC<{
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  badge?: React.ReactNode;
  /** Tinted top-edge — 'cool' for cyan, 'warm' for amber, 'danger' for red */
  tint?: 'cool' | 'warm' | 'danger' | 'none';
  /** Heavier blur + brighter top-edge for the page hero */
  hero?: boolean;
  /** Stagger delay (seconds) for entrance — applied as motion delay */
  stagger?: number;
  /** Plain section name shown as eyebrow above the title */
  eyebrow?: string;
  /** Right-aligned content (numbers, metadata) in the header band */
  trailing?: React.ReactNode;
}> = ({ title, icon, children, badge, tint = 'none', hero = false, stagger = 0, eyebrow, trailing }) => {
  const { isDarkMode } = useTheme();
  // Tint = a subtle accent border on the top edge, keyed to the section
  // semantic. Stays in the existing navy palette other pages use.
  const tintBorder =
    !isDarkMode
      ? tint === 'danger' ? 'border-red-200'
        : tint === 'warm' ? 'border-amber-200'
        : tint === 'cool' ? 'border-frost-200'
        : 'border-gray-200'
      : tint === 'cool' ? 'border-frost-500/20'
      : tint === 'warm' ? 'border-amber-500/20'
      : tint === 'danger' ? 'border-red-500/20'
      : 'border-white/10';
  const eyebrowColor =
    !isDarkMode
      ? tint === 'danger' ? 'text-red-600/90'
        : tint === 'warm' ? 'text-amber-700/90'
        : 'text-frost-600/90'
      : tint === 'danger' ? 'text-red-300/90'
      : tint === 'warm' ? 'text-amber-300/90'
      : 'text-frost-300/90';

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: stagger, ease: [0.22, 1, 0.36, 1] }}
      className={`relative rounded-2xl border overflow-hidden ${
        hero ? 'p-7 sm:p-10' : 'p-5 sm:p-7'
      } ${
        isDarkMode
          ? `bg-navy-800/80 ${tintBorder} shadow-[0_0_40px_-12px_rgba(56,189,248,0.06)]`
          : `bg-white ${tintBorder} shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.06)]`
      }`}
    >
      <div className="flex items-start justify-between mb-5 sm:mb-6 flex-wrap gap-3">
        <div className="min-w-0 flex-1">
          {eyebrow && (
            <p className={`text-[10px] font-semibold uppercase tracking-[0.16em] mb-1.5 ${eyebrowColor}`}>
              {eyebrow}
            </p>
          )}
          <div className="flex items-center gap-2.5 flex-wrap">
            {icon && (
              <span
                className={`flex-shrink-0 ${
                  isDarkMode ? 'text-frost-300/90' : 'text-frost-600'
                }`}
                aria-hidden="true"
              >
                {icon}
              </span>
            )}
            <h3 className={`text-[19px] sm:text-[22px] font-semibold tracking-[-0.01em] leading-none ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {title}
            </h3>
            {badge}
          </div>
        </div>
        {trailing && (
          <div className="flex-shrink-0">{trailing}</div>
        )}
      </div>
      {children}
    </motion.section>
  );
};

const LiveBadge: React.FC = () => {
  const { isDarkMode } = useTheme();
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider ${
      isDarkMode
        ? 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30'
        : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
    }`}>
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      Live
    </span>
  );
};

const EmptyHint: React.FC<{
  icon?: React.ReactNode;
  title: string;
  hint?: string;
}> = ({ icon, title, hint }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className={`flex flex-col items-center text-center py-8 px-4 rounded-xl ${
      isDarkMode ? 'bg-navy-700/20' : 'bg-gray-50'
    }`}>
      {icon && (
        <div className={`mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {icon}
        </div>
      )}
      <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        {title}
      </p>
      {hint && (
        <p className={`text-xs mt-1 max-w-md ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          {hint}
        </p>
      )}
    </div>
  );
};

const fmtChapter = (
  num: number | null | undefined,
  name: string | null | undefined,
): string => {
  if (num != null && name) return `Ch ${num}: ${name}`;
  if (name) return name;
  if (num != null) return `Chapter ${num}`;
  return '';
};

// ─── M9: At-a-Glance Header ────────────────────────────────────────────────

const DIM_META: Record<string, { label: string; darkAccent: string; lightAccent: string }> = {
  reading: {
    label: 'Reading',
    darkAccent: 'bg-frost-500/15 text-frost-300 ring-1 ring-frost-400/30',
    lightAccent: 'bg-frost-50 text-frost-700 ring-1 ring-frost-200',
  },
  calibration: {
    label: 'Confidence',
    darkAccent: 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/30',
    lightAccent: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  },
  qa_evolution: {
    label: 'Questions',
    darkAccent: 'bg-violet-500/15 text-violet-300 ring-1 ring-violet-400/30',
    lightAccent: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200',
  },
  at_risk: {
    label: 'At Risk',
    darkAccent: 'bg-red-500/15 text-red-300 ring-1 ring-red-400/30',
    lightAccent: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  },
};

const AiSummaryHeader: React.FC<{ bullets: AiSummaryBullet[] }> = ({ bullets }) => {
  const { isDarkMode } = useTheme();
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className={`relative rounded-2xl border p-7 sm:p-10 overflow-hidden ${
        isDarkMode
          ? 'bg-gradient-to-br from-navy-800/95 via-navy-800/85 to-frost-900/40 border-frost-500/20'
          : 'bg-gradient-to-br from-white via-frost-50/50 to-frost-100/40 border-frost-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_36px_-12px_rgba(6,182,212,0.10)]'
      }`}
    >
      {/* Soft luminous halos — sit behind content, give the hero its glow */}
      <div className={`absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-45 pointer-events-none ${
        isDarkMode ? 'bg-frost-500/30' : 'bg-frost-300/40'
      }`} aria-hidden="true" />
      <div className={`absolute -bottom-16 -left-16 w-48 h-48 rounded-full blur-3xl opacity-25 pointer-events-none ${
        isDarkMode ? 'bg-violet-500/20' : 'bg-violet-200/40'
      }`} aria-hidden="true" />

      <div className="relative">
        <div className="flex items-center gap-2 mb-7">
          <SparklesIcon className={`w-3.5 h-3.5 ${isDarkMode ? 'text-frost-300' : 'text-frost-600'}`} aria-hidden="true" />
          <h2 className={`text-[10px] font-semibold uppercase tracking-[0.16em] ${
            isDarkMode ? 'text-frost-300/90' : 'text-frost-700/90'
          }`}>
            At a glance · this week
          </h2>
          <span className={`text-[9px] px-1.5 py-[1px] rounded font-semibold uppercase tracking-wider ${
            isDarkMode ? 'bg-violet-500/15 text-violet-300 ring-1 ring-violet-400/20' : 'bg-violet-50 text-violet-700 ring-1 ring-violet-200'
          }`}>AI</span>
        </div>

        {bullets.length === 0 ? (
          <EmptyHint
            icon={<SparklesIcon className="w-7 h-7" />}
            title="Summary not yet generated"
            hint="The weekly summary builds each Sunday. Once chat / reading / Mnemo signal accrues, four bullet beats will appear here."
          />
        ) : (
          <ul className="space-y-5">
            {bullets.map((b, i) => {
              const meta = DIM_META[b.dimension] || DIM_META.reading;
              const accentClass = isDarkMode ? meta.darkAccent : meta.lightAccent;
              return (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="flex gap-3.5 sm:gap-4"
                >
                  <span className={`flex-shrink-0 text-[9px] font-bold uppercase tracking-[0.14em] mt-[5px] px-2 py-[3px] rounded-full whitespace-nowrap self-start ${accentClass}`}>
                    {meta.label}
                  </span>
                  <p className={`text-[15px] sm:text-[16px] leading-[1.6] font-normal ${
                    isDarkMode ? 'text-gray-100' : 'text-gray-800'
                  }`}>
                    {b.text}
                  </p>
                </motion.li>
              );
            })}
          </ul>
        )}
      </div>
    </motion.div>
  );
};

// ─── M5: Reading Time This Week — Stacked Bar Chart ────────────────────────

const CHAPTER_PALETTE = [
  '#0ea5e9', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899',
  '#06b6d4', '#84cc16', '#f97316', '#a855f7', '#22d3ee',
];

const chapterColor = (chapterNumber: string | number): string => {
  const n = typeof chapterNumber === 'number' ? chapterNumber : parseInt(chapterNumber, 10);
  if (isNaN(n)) return CHAPTER_PALETTE[0];
  return CHAPTER_PALETTE[(n - 1 + CHAPTER_PALETTE.length) % CHAPTER_PALETTE.length];
};

const DAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const truncate = (s: string, n: number) => (s.length > n ? s.slice(0, n - 1) + '…' : s);

const chapterLabel = (
  num: string,
  chapterMap: Record<string, string> | undefined,
): string => {
  const name = chapterMap?.[num];
  if (name) return `Ch ${num} · ${truncate(name, 28)}`;
  return `Ch ${num}`;
};

const buildReadingChartData = (
  reading_breakdown: Record<string, Record<string, number>>,
  weekStart: string | null,
  chapterMap: Record<string, string> | undefined,
): { rows: any[]; chapters: { key: string; chapterNum: string }[] } => {
  const days: { date: string; label: string }[] = [];
  if (weekStart) {
    const start = new Date(weekStart + 'T00:00:00');
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push({ date: d.toISOString().slice(0, 10), label: DAYS_SHORT[i] });
    }
  } else {
    Object.keys(reading_breakdown || {}).sort().forEach((iso) => {
      const d = new Date(iso + 'T00:00:00');
      const dayIdx = (d.getDay() + 6) % 7;
      days.push({ date: iso, label: DAYS_SHORT[dayIdx] });
    });
  }

  const chapterSet = new Set<string>();
  Object.values(reading_breakdown || {}).forEach((dayMap) => {
    Object.keys(dayMap || {}).forEach((ch) => chapterSet.add(ch));
  });
  const chapterNums = Array.from(chapterSet).sort(
    (a, b) => parseInt(a) - parseInt(b),
  );

  const chapters = chapterNums.map((num) => ({
    key: chapterLabel(num, chapterMap),
    chapterNum: num,
  }));

  const rows = days.map(({ date, label }) => {
    const dayMap = reading_breakdown[date] || {};
    const row: any = { day: label, date };
    chapters.forEach(({ key, chapterNum }) => {
      row[key] = dayMap[chapterNum] || 0;
    });
    return row;
  });

  return { rows, chapters };
};

// Pulled from the dataKey (e.g. "Ch 7 · …") so we can resolve the real chapter
// color in the tooltip — the bar uses a gradient fill (`url(#g-N)`), which makes
// recharts' `payload[i].color` an unusable URL string.
const CHAPTER_NUM_RE = /^Ch\s+(\d+)/;

const ReadingTooltip: React.FC<any> = ({ active, payload, label }) => {
  const { isDarkMode } = useTheme();
  if (!active || !payload?.length) return null;
  const total = payload.reduce((sum: number, p: any) => sum + (p.value || 0), 0);
  return (
    <div className={`rounded-xl px-4 py-3 shadow-2xl border backdrop-blur-md ${
      isDarkMode ? 'bg-navy-800/95 border-white/10' : 'bg-white/95 border-gray-200'
    }`}>
      <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        {label} &middot; <span className="tabular-nums">{total} min total</span>
      </p>
      {payload.map((p: any, i: number) => {
        if (!p.value) return null;
        const m = String(p.dataKey || p.name || '').match(CHAPTER_NUM_RE);
        const swatch = m ? chapterColor(m[1]) : (p.fill || p.color);
        return (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span
              className="w-3 h-3 rounded-sm flex-shrink-0 ring-1 ring-white/10"
              style={{
                background: `linear-gradient(180deg, ${swatch} 0%, ${swatch}aa 100%)`,
              }}
            />
            <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{p.name}</span>
            <span className={`ml-auto pl-3 font-medium tabular-nums ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>{p.value} min</span>
          </div>
        );
      })}
    </div>
  );
};

const ReadingCard: React.FC<{
  reading_breakdown: Record<string, Record<string, number>>;
  total_minutes: number;
  active_days: number;
  week_start: string | null;
  chapter_map: Record<string, string> | undefined;
}> = ({ reading_breakdown, total_minutes, active_days, week_start, chapter_map }) => {
  const { isDarkMode } = useTheme();
  const { rows, chapters } = useMemo(
    () => buildReadingChartData(reading_breakdown, week_start, chapter_map),
    [reading_breakdown, week_start, chapter_map],
  );
  const hasAny = chapters.length > 0;

  return (
    <Section
      eyebrow="This week"
      title="Reading"
      icon={<ClockIcon className="w-5 h-5" />}
      tint="cool"
      stagger={0.08}
      trailing={
        hasAny ? (
          <div className="flex items-center gap-5">
            <div className="text-right">
              <div className={`text-3xl font-bold tabular-nums leading-none tracking-tight ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>{total_minutes}</div>
              <div className={`text-[10px] uppercase tracking-wider mt-1.5 font-medium ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>min total</div>
            </div>
            <div className={`h-10 w-px ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`} />
            <div className="text-right">
              <div className={`text-3xl font-bold tabular-nums leading-none tracking-tight ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>{active_days}</div>
              <div className={`text-[10px] uppercase tracking-wider mt-1.5 font-medium ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>{active_days === 1 ? 'day active' : 'days active'}</div>
            </div>
          </div>
        ) : undefined
      }
    >
      {!hasAny ? (
        <EmptyHint
          icon={<BookOpenIcon className="w-7 h-7" />}
          title="No chapter-attributed reading this week"
          hint="Pre-instrumentation sessions (before Apr 28) didn't track per-page dwell. Forward-going reading will populate here."
        />
      ) : (
        <div className="h-80 sm:h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} barCategoryGap="22%" margin={{ top: 8, right: 8, left: 0, bottom: 32 }}>
              <defs>
                {chapters.map(({ key, chapterNum }) => {
                  const c = chapterColor(chapterNum);
                  const safeId = `g-${chapterNum}`;
                  return (
                    <linearGradient key={safeId} id={safeId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={c} stopOpacity={0.95} />
                      <stop offset="100%" stopColor={c} stopOpacity={0.65} />
                    </linearGradient>
                  );
                })}
              </defs>
              <CartesianGrid
                strokeDasharray="2 4"
                stroke={isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.05)'}
                vertical={false}
              />
              <XAxis
                dataKey="day"
                tick={{ fill: isDarkMode ? '#64748B' : '#94A3B8', fontSize: 11, fontWeight: 500 }}
                axisLine={false}
                tickLine={false}
                dy={6}
              />
              <YAxis
                tick={{ fill: isDarkMode ? '#64748B' : '#94A3B8', fontSize: 11, fontWeight: 500 }}
                axisLine={false}
                tickLine={false}
                unit="m"
                width={36}
              />
              <Tooltip
                content={<ReadingTooltip />}
                cursor={{ fill: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)', radius: 6 }}
              />
              <Legend
                iconType="circle"
                iconSize={7}
                verticalAlign="bottom"
                align="left"
                wrapperStyle={{
                  fontSize: '11px',
                  color: isDarkMode ? '#94A3B8' : '#64748B',
                  paddingTop: '20px',
                  fontWeight: 500,
                  lineHeight: '1.6',
                }}
              />
              {chapters.map(({ key, chapterNum }, i) => (
                <Bar
                  key={chapterNum}
                  dataKey={key}
                  stackId="reading"
                  fill={`url(#g-${chapterNum})`}
                  radius={i === chapters.length - 1 ? [5, 5, 0, 0] : [0, 0, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Section>
  );
};

// ─── M6: Confidence Crash Map — Interactive 2×2 Quadrant ───────────────────

type QuadrantKey = 'unsure_wrong' | 'confident_wrong' | 'unsure_correct' | 'confident_correct';

const tierBadge = (tier: string, isDark: boolean) => {
  if (tier === 'red') return isDark ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700';
  if (tier === 'yellow') return isDark ? 'bg-yellow-500/20 text-yellow-300' : 'bg-yellow-100 text-yellow-700';
  return isDark ? 'bg-gray-500/20 text-gray-300' : 'bg-gray-100 text-gray-700';
};

// ─── M6 helpers: punch-card cells + readout strip ──────────────────────────
// The danger-row default is collapsed: tier + concept + plain-English sentence.
// On click, reveals per-attempt evidence as labeled cells (no glyph encoding —
// each cell shows the literal rating + outcome word) plus a readout strip
// drawing on avg_confidence / last_actual / latest_stability / due_in_days.
// Pattern follows Holstein 2017 + Carnegie MATHia / i-Ready: glance layer is
// plain text, evidence is one click away.

type CellScheme = {
  bg: string;
  border: string;
  numColor: string;
  iconColor: string;
  labelColor: string;
  icon: string;
  label: string;
};

const outcomeIcon = (a: CalibrationActual) =>
  a === 'got_it' ? '✓' : a === 'fuzzy' ? '~' : '✗';
const outcomeLabel = (a: CalibrationActual) =>
  a === 'got_it' ? 'got it' : a;

// Inline definition pattern: dotted-underline flags terms-of-art; the browser's
// native title shows the plain-English meaning on hover/long-press. Accessible
// out of the box (screen readers announce title) and survives parent
// overflow:hidden — both reasons not to reach for a portal/popover library.
const TermDef: React.FC<{
  label: React.ReactNode;
  definition: string;
  className?: string;
}> = ({ label, definition, className = '' }) => {
  const { isDarkMode } = useTheme();
  return (
    <span
      title={definition}
      tabIndex={0}
      className={`cursor-help underline decoration-dotted decoration-1 underline-offset-[3px] ${
        isDarkMode ? 'decoration-frost-400/50 hover:decoration-frost-300' : 'decoration-frost-500/50 hover:decoration-frost-600'
      } ${className}`}
    >
      {label}
    </span>
  );
};

// FSRS `stability` is approximately the predicted retention half-life in days.
// Tutors don't speak FSRS, so translate to plain English. The raw number is
// preserved as a tooltip via `title` for power users.
const memoryStrengthLabel = (
  stability: number | null | undefined,
): { label: string; color: (isDark: boolean) => string } => {
  const s = stability ?? 0;
  if (s < 1) return {
    label: 'fades in hours',
    color: (d) => d ? 'text-red-300' : 'text-red-600',
  };
  if (s < 5) return {
    label: 'shaky',
    color: (d) => d ? 'text-amber-300' : 'text-amber-600',
  };
  if (s < 14) return {
    label: 'building',
    color: (d) => d ? 'text-yellow-300' : 'text-yellow-700',
  };
  return {
    label: 'durable',
    color: (d) => d ? 'text-emerald-300' : 'text-emerald-700',
  };
};

const cellScheme = (e: CalibrationEvent, isDark: boolean): CellScheme => {
  const high = e.confidence >= 4;
  const icon = outcomeIcon(e.actual);
  const label = outcomeLabel(e.actual);
  const base = (
    bg: string, border: string, num: string, iconC: string, labelC: string,
  ): CellScheme => ({ bg, border, numColor: num, iconColor: iconC, labelColor: labelC, icon, label });

  // Six combos (high/low confidence × got/fuzzy/forgot).
  if (high && e.actual === 'forgot') {
    return base(
      isDark ? 'bg-red-500/15' : 'bg-red-50',
      isDark ? 'border-red-500/45' : 'border-red-300',
      isDark ? 'text-red-200' : 'text-red-800',
      isDark ? 'text-red-300' : 'text-red-600',
      isDark ? 'text-red-300/90' : 'text-red-700',
    );
  }
  if (high && e.actual === 'fuzzy') {
    return base(
      isDark ? 'bg-amber-500/12' : 'bg-amber-50',
      isDark ? 'border-amber-500/35' : 'border-amber-300',
      isDark ? 'text-amber-200' : 'text-amber-800',
      isDark ? 'text-amber-300' : 'text-amber-600',
      isDark ? 'text-amber-300/90' : 'text-amber-700',
    );
  }
  if (high && e.actual === 'got_it') {
    return base(
      isDark ? 'bg-emerald-500/10' : 'bg-emerald-50',
      isDark ? 'border-emerald-500/30' : 'border-emerald-200',
      isDark ? 'text-emerald-200' : 'text-emerald-800',
      isDark ? 'text-emerald-300' : 'text-emerald-600',
      isDark ? 'text-emerald-300/90' : 'text-emerald-700',
    );
  }
  if (!high && e.actual === 'got_it') {
    // "lucky" — knew it but didn't trust themselves
    return base(
      isDark ? 'bg-frost-500/10' : 'bg-sky-50',
      isDark ? 'border-frost-500/30' : 'border-sky-200',
      isDark ? 'text-frost-200' : 'text-sky-800',
      isDark ? 'text-emerald-300' : 'text-emerald-600',
      isDark ? 'text-frost-300/90' : 'text-sky-700',
    );
  }
  // low + fuzzy / forgot → neutral (expected miss / hedge)
  return base(
    isDark ? 'bg-navy-700/30' : 'bg-gray-50',
    isDark ? 'border-white/10' : 'border-gray-200',
    isDark ? 'text-gray-200' : 'text-gray-700',
    isDark ? 'text-gray-400' : 'text-gray-500',
    isDark ? 'text-gray-400' : 'text-gray-500',
  );
};

const PunchCell: React.FC<{
  event: CalibrationEvent;
  isLatest: boolean;
  delay: number;
}> = ({ event, isLatest, delay }) => {
  const { isDarkMode } = useTheme();
  const s = cellScheme(event, isDarkMode);
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className={`relative flex flex-col items-center justify-center gap-1 rounded-lg border px-3.5 py-2.5 min-w-[68px] ${s.bg} ${s.border}`}
    >
      <div className="flex items-baseline gap-1.5">
        <span className={`text-[19px] font-bold tabular-nums leading-none tracking-tight ${s.numColor}`}>
          {event.confidence}
        </span>
        <span className={`text-[15px] font-bold leading-none ${s.iconColor}`} aria-hidden="true">{s.icon}</span>
      </div>
      <span className={`text-[9px] font-bold uppercase tracking-[0.14em] ${s.labelColor}`}>
        {s.label}
      </span>
      {isLatest && (
        <span className="absolute -top-1 -right-1 flex h-2 w-2" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-frost-400 opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-frost-500" />
        </span>
      )}
    </motion.div>
  );
};

// Build the plain-English collapsed-row summary from sparkline_events when
// available, falling back to the legacy numeric sparkline for back-compat.
const buildDangerSummary = (
  d: OverconfidenceDangerConcept,
): { highSure: number; total: number; forgot: number; fuzzy: number; got: number } => {
  const events = d.sparkline_events || [];
  if (events.length > 0) {
    return {
      highSure: events.filter((e) => e.confidence >= 4).length,
      total: events.length,
      forgot: events.filter((e) => e.actual === 'forgot').length,
      fuzzy: events.filter((e) => e.actual === 'fuzzy').length,
      got: events.filter((e) => e.actual === 'got_it').length,
    };
  }
  const numeric = d.sparkline || [];
  let forgot = 0, fuzzy = 0, got = 0;
  numeric.forEach((s) => {
    if (s >= 0.99) got += 1;
    else if (s >= 0.5) fuzzy += 1;
    else forgot += 1;
  });
  // Without paired data we can't count high-conf precisely; assume all
  // qualifying (since this row is in the danger zone by definition).
  return { highSure: numeric.length, total: numeric.length, forgot, fuzzy, got };
};

const QuadrantCell: React.FC<{
  count: number;
  total: number;
  label: string;
  sub: string;
  variant: 'danger' | 'good' | 'neutral-warn' | 'neutral-good';
  icon?: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}> = ({ count, total, label, sub, variant, icon, selected, onClick }) => {
  const { isDarkMode } = useTheme();
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;

  const colorMap = {
    danger: {
      dark: 'bg-gradient-to-br from-red-500/30 to-red-700/15 border-red-500/40',
      light: 'bg-gradient-to-br from-red-100/80 to-red-50 border-red-300',
      darkText: 'text-red-300', lightText: 'text-red-700',
      ringDark: 'ring-red-400/60 shadow-[0_0_30px_-5px_rgba(248,113,113,0.45)]',
      ringLight: 'ring-red-400 shadow-[0_4px_24px_-4px_rgba(220,38,38,0.25)]',
    },
    good: {
      dark: 'bg-gradient-to-br from-emerald-500/20 to-emerald-700/10 border-emerald-500/30',
      light: 'bg-gradient-to-br from-emerald-50 to-white border-emerald-200',
      darkText: 'text-emerald-300', lightText: 'text-emerald-700',
      ringDark: 'ring-emerald-400/60', ringLight: 'ring-emerald-400',
    },
    'neutral-warn': {
      dark: 'bg-gradient-to-br from-amber-500/15 to-amber-700/5 border-amber-500/25',
      light: 'bg-gradient-to-br from-amber-50 to-white border-amber-200',
      darkText: 'text-amber-300', lightText: 'text-amber-700',
      ringDark: 'ring-amber-400/60', ringLight: 'ring-amber-400',
    },
    'neutral-good': {
      dark: 'bg-gradient-to-br from-frost-500/15 to-frost-700/5 border-frost-500/25',
      light: 'bg-gradient-to-br from-frost-50 to-white border-frost-200',
      darkText: 'text-frost-300', lightText: 'text-frost-700',
      ringDark: 'ring-frost-400/60', ringLight: 'ring-frost-400',
    },
  } as const;

  const cfg = colorMap[variant];
  const cls = isDarkMode ? cfg.dark : cfg.light;
  const txt = isDarkMode ? cfg.darkText : cfg.lightText;
  const ring = isDarkMode ? cfg.ringDark : cfg.ringLight;

  return (
    <motion.button
      onClick={onClick}
      whileTap={{ scale: 0.99 }}
      className={`group relative w-full text-left rounded-xl border p-4 sm:p-[18px] overflow-hidden transition-all duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-frost-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900 ${cls} ${
        selected ? `ring-2 ${ring}` : 'opacity-[0.92] hover:opacity-100'
      }`}
    >
      {variant === 'danger' && !selected && count > 0 && (
        <span className="absolute top-3 right-3 flex h-1.5 w-1.5" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-60" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-red-500" />
        </span>
      )}
      {icon && (
        <div className={`absolute top-3 right-3 ${txt} opacity-70 transition-opacity group-hover:opacity-90`} aria-hidden="true">
          {icon}
        </div>
      )}
      <div className="flex items-baseline gap-1.5 mb-1.5">
        <span className={`text-[28px] sm:text-[32px] font-bold tabular-nums leading-none tracking-[-0.02em] ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>{count}</span>
        <span className={`text-[11px] tabular-nums font-medium ${
          isDarkMode ? 'text-gray-500' : 'text-gray-500'
        }`}>· {pct}%</span>
      </div>
      <p className={`text-[13px] font-semibold leading-tight ${txt}`}>{label}</p>
      <p className={`text-[11px] mt-1 leading-tight ${
        isDarkMode ? 'text-gray-400/70' : 'text-gray-500/80'
      }`}>{sub}</p>
    </motion.button>
  );
};

// One row in the danger-zone list. Collapsed by default — tier + concept +
// plain-English glance line + due chip. Click to expand: punch-card cells per
// attempt + readout strip. Pattern follows Holstein 2017 ("Glance, Plan, Act"):
// glance is text, evidence is one click away.
const DangerRow: React.FC<{
  d: OverconfidenceDangerConcept;
  index: number;
}> = ({ d, index }) => {
  const { isDarkMode } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const events = d.sparkline_events || [];
  const summary = buildDangerSummary(d);

  const dueLabel = d.due_in_days == null ? null
    : d.due_in_days < 0 ? `${Math.abs(d.due_in_days)}d overdue`
    : d.due_in_days === 0 ? 'due today'
    : `due in ${d.due_in_days}d`;
  const dueIsBad = d.due_in_days != null && d.due_in_days <= 0;

  const lastActualColor =
    d.last_actual === 'forgot' ? (isDarkMode ? 'text-red-300' : 'text-red-600')
    : d.last_actual === 'fuzzy' ? (isDarkMode ? 'text-amber-300' : 'text-amber-600')
    : isDarkMode ? 'text-emerald-300' : 'text-emerald-600';

  // Glance-layer sentence — derived from sparkline_events when present.
  const { highSure, total, forgot, fuzzy, got } = summary;
  const leadClause =
    total === 0 ? '—'
    : highSure === total ? `Said sure ${total === 1 ? 'once' : `${total} times`}`
    : highSure > 0 ? `Said sure on ${highSure} of ${total}`
    : `${total} ${total === 1 ? 'attempt' : 'attempts'} in window`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.025, 0.3), duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className={`relative rounded-xl border overflow-hidden transition-colors ${
        isDarkMode
          ? 'bg-red-500/[0.04] border-red-500/15 hover:border-red-500/25'
          : 'bg-red-50/40 border-red-200/80 hover:border-red-300'
      }`}
    >
      {/* Header — clickable to toggle */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={`w-full text-left px-4 sm:px-5 py-3.5 flex items-start sm:items-center justify-between gap-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400/50 focus-visible:ring-inset ${
          isDarkMode ? 'hover:bg-white/[0.02]' : 'hover:bg-red-50/40'
        }`}
        aria-expanded={expanded}
      >
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <span className={`mt-1 text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-[3px] rounded flex-shrink-0 ${tierBadge(d.tier, isDarkMode)}`}>
            {d.tier}
          </span>
          <div className="min-w-0 flex-1">
            {/* Concept name on its own line so it doesn't wrap mid-word
                with the chapter context on narrow widths. */}
            <h4 className={`text-[14px] font-semibold tracking-tight leading-snug ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {d.concept}
            </h4>
            {(d.chapter_number != null || d.chapter_name) && (
              <p className={`text-[11px] mt-0.5 ${
                isDarkMode ? 'text-gray-500' : 'text-gray-500'
              }`}>
                {fmtChapter(d.chapter_number, d.chapter_name)}
              </p>
            )}
            <p className={`text-[13px] leading-[1.5] mt-1.5 ${
              isDarkMode ? 'text-gray-300/95' : 'text-gray-700'
            }`}>
              {leadClause}
              {forgot > 0 && (
                <>, <span className={`font-semibold ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>
                  forgot {forgot} of {total}
                </span></>
              )}
              {fuzzy > 0 && (
                <>{forgot > 0 ? ' · ' : ', '}<span className={`font-semibold ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}>
                  fuzzy on {fuzzy}
                </span></>
              )}
              {got > 0 && (forgot > 0 || fuzzy > 0) && (
                <> · <span className={`font-semibold ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>
                  got {got}
                </span></>
              )}
              {d.last_actual && (
                <span className={`ml-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {' '}(last: <span className={lastActualColor}>{d.last_actual.replace('_', ' ')}</span>)
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1.5 sm:gap-2 flex-shrink-0">
          {dueLabel && (
            <span className={`text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-[3px] rounded whitespace-nowrap ${
              dueIsBad
                ? isDarkMode ? 'bg-red-500/20 text-red-300 ring-1 ring-red-400/20' : 'bg-red-100 text-red-700 ring-1 ring-red-200'
                : isDarkMode ? 'bg-white/[0.04] text-gray-400' : 'bg-gray-100 text-gray-600'
            }`}>
              {dueLabel}
            </span>
          )}
          <ChevronDownIcon
            className={`w-4 h-4 transition-transform duration-200 ${
              expanded ? 'rotate-180' : ''
            } ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}
            aria-hidden="true"
          />
        </div>
      </button>

      {/* Expanded evidence panel */}
      {expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          className={`overflow-hidden border-t ${
            isDarkMode ? 'border-white/[0.04]' : 'border-red-100'
          }`}
        >
          <div className="px-4 sm:px-5 py-4 space-y-3">
            {events.length > 0 ? (
              <div>
                <p className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  Last {events.length} {events.length === 1 ? 'attempt' : 'attempts'} · oldest → newest
                </p>
                <div className="flex items-stretch gap-2 flex-wrap">
                  {events.map((e, k) => (
                    <PunchCell
                      key={k}
                      event={e}
                      isLatest={k === events.length - 1}
                      delay={k * 0.04}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <p className={`text-xs italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Per-attempt history will populate after the next pipeline run.
              </p>
            )}

            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {d.avg_confidence != null && (
                <>avg confidence <span className={`font-semibold tabular-nums ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>{d.avg_confidence.toFixed(1)}/5</span> · </>
              )}
              <TermDef
                label="memory"
                definition="How durable the memory is right now, predicted by the spaced-repetition algorithm. 'fades in hours' = will be forgotten the same day; 'durable' = solid for 2+ weeks."
              />{' '}
              <span
                className={`font-semibold ${memoryStrengthLabel(d.latest_stability).color(isDarkMode)}`}
                title={`FSRS stability: ${(d.latest_stability ?? 0).toFixed(2)} days`}
              >
                {memoryStrengthLabel(d.latest_stability).label}
              </span>
              {' '}·{' '}
              <TermDef
                label={`${d.n_reps} ${d.n_reps === 1 ? 'rep' : 'reps'} in window`}
                definition="Number of Mnemo self-reviews of this concept in the last 4 weeks. The danger list requires at least 2 — fewer is too little evidence to call a pattern."
              />
            </p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

const QuadrantDrawer: React.FC<{
  selectedKey: QuadrantKey;
  count: number;
  dangerConcepts: OverconfidenceDangerConcept[];
}> = ({ selectedKey, count, dangerConcepts }) => {
  const { isDarkMode } = useTheme();

  const headerByKey: Record<QuadrantKey, { title: string; sub: string }> = {
    confident_wrong: {
      title: 'Confident & Wrong — danger zone',
      sub: 'Concepts the student confidently rated themselves on, then got wrong on Mnemo. These need attention before next session.',
    },
    confident_correct: {
      title: 'Confident & Correct — mastered',
      sub: 'Well-calibrated wins. Confidence matched outcome. Keep these on a long review interval.',
    },
    unsure_correct: {
      title: 'Unsure & Correct — lucky guesses',
      sub: 'Student knew it but didn\'t trust themselves. Worth a confidence-building reinforcement.',
    },
    unsure_wrong: {
      title: 'Unsure & Wrong — expected misses',
      sub: 'Honest "I don\'t know" outcomes. Calibration is fine here — just unfamiliar material.',
    },
  };

  const head = headerByKey[selectedKey];
  const showsConcepts = selectedKey === 'confident_wrong' && dangerConcepts.length > 0;

  return (
    <motion.div
      key={selectedKey}
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className={`mt-5 rounded-xl border p-5 sm:p-6 ${
        isDarkMode
          ? 'bg-navy-900/40 border-white/[0.06]'
          : 'bg-gray-50/70 border-gray-200/80'
      }`}>
        <div className="mb-4">
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className={`text-[15px] font-semibold tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {head.title}
            </p>
            <span className={`text-[11px] tabular-nums font-medium ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {count} {count === 1 ? 'event' : 'events'}
              {selectedKey === 'confident_wrong' && (
                <>
                  {' '}→{' '}
                  <span className={`font-semibold ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>
                    {dangerConcepts.length} {dangerConcepts.length === 1 ? 'concept' : 'concepts'}
                  </span>
                </>
              )}
            </span>
          </div>
          <p className={`text-[12px] mt-1 leading-[1.55] ${isDarkMode ? 'text-gray-400/90' : 'text-gray-500'}`}>
            {head.sub}
          </p>
        </div>

        {!showsConcepts && selectedKey === 'confident_correct' ? (
          <div className={`flex items-start gap-3 p-4 rounded-lg ${
            isDarkMode ? 'bg-emerald-500/5 border border-emerald-500/10' : 'bg-emerald-50/60 border border-emerald-100'
          }`}>
            <CheckBadgeIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
              isDarkMode ? 'text-emerald-300' : 'text-emerald-600'
            }`} />
            <div className="text-xs leading-relaxed">
              <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                <span className={`font-semibold tabular-nums ${
                  isDarkMode ? 'text-emerald-300' : 'text-emerald-700'
                }`}>{count}</span>{' '}
                <span className="font-medium">review events</span> where confidence matched outcome.
                Per-concept lists are only surfaced for the <span className="font-medium">danger zone</span> —
                this is the goal state, not a missing chart.
              </p>
            </div>
          </div>
        ) : showsConcepts ? (
          <div className="space-y-2">
            {dangerConcepts.map((d, i) => (
              <DangerRow key={`${d.concept}-${i}`} d={d} index={i} />
            ))}
          </div>
        ) : (
          <div className={`flex items-center justify-center py-6 px-4 rounded-lg ${
            isDarkMode ? 'bg-navy-800/40' : 'bg-white'
          }`}>
            <p className={`text-xs italic text-center max-w-md ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`}>
              Per-concept breakdown for this quadrant isn't surfaced individually — the calibration pipeline only flags the danger zone.
              Click <span className="not-italic font-medium">Confident &amp; Wrong</span> for the actionable list.
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const CalibrationCard: React.FC<{
  data: { stats?: any; quadrant?: any; danger_concepts?: OverconfidenceDangerConcept[] };
}> = ({ data }) => {
  const { isDarkMode } = useTheme();
  const stats = data?.stats;
  const quadrant = data?.quadrant;
  const danger = data?.danger_concepts || [];
  const hasData = !!stats?.total_reviews;

  const cc = quadrant?.confident_correct || 0;
  const cw = quadrant?.confident_wrong || 0;
  const uc = quadrant?.unsure_correct || 0;
  const uw = quadrant?.unsure_wrong || 0;
  const total = cc + cw + uc + uw;

  // Default-select the danger cell if it has events; else mastered.
  const [selected, setSelected] = useState<QuadrantKey>(
    cw > 0 ? 'confident_wrong' : 'confident_correct',
  );

  const overRate = hasData && stats.overconfidence_rate != null
    ? Math.round((stats.overconfidence_rate || 0) * 100)
    : 0;

  const countByKey: Record<QuadrantKey, number> = {
    confident_wrong: cw,
    confident_correct: cc,
    unsure_correct: uc,
    unsure_wrong: uw,
  };

  return (
    <Section
      eyebrow="Where confidence misses"
      title="Confidence Crash Map"
      icon={<ScaleIcon className="w-5 h-5" />}
      tint="danger"
      stagger={0.16}
      badge={
        hasData && stats.overconfidence_events > 0 ? (
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
            overRate > 25
              ? isDarkMode ? 'bg-red-500/15 text-red-300 ring-1 ring-red-400/30' : 'bg-red-50 text-red-700 ring-1 ring-red-200'
              : isDarkMode ? 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/30' : 'bg-amber-50 text-amber-800 ring-1 ring-amber-200'
          }`}>
            {overRate}% overconfidence
          </span>
        ) : undefined
      }
    >
      {!hasData ? (
        <EmptyHint
          icon={<ScaleIcon className="w-7 h-7" />}
          title="No Mnemo activity this week"
          hint="Calibration is measured from confidence ratings made before reveal in Mnemo reviews."
        />
      ) : (
        <>
          {/* Stat strip — horizontal on sm+ with hairlines, grid on mobile so
              numbers don't wrap mid-stat. Order: total → events → concepts. */}
          <div className={`mb-6 grid grid-cols-3 sm:flex sm:items-stretch sm:flex-wrap gap-3 sm:gap-x-5 sm:gap-y-3 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            <div className="min-w-0">
              <div className={`text-[20px] sm:text-[22px] font-bold tabular-nums leading-none tracking-tight ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>{stats.total_reviews}</div>
              <div className={`text-[10px] uppercase tracking-[0.14em] mt-1.5 font-semibold truncate ${
                isDarkMode ? 'text-gray-500' : 'text-gray-500'
              }`}>self-reviews</div>
            </div>
            {stats.overconfidence_events > 0 && (
              <>
                <div className={`hidden sm:block w-px ${isDarkMode ? 'bg-white/[0.08]' : 'bg-gray-200'}`} aria-hidden="true" />
                <div className="min-w-0">
                  <div className={`text-[20px] sm:text-[22px] font-bold tabular-nums leading-none tracking-tight ${
                    isDarkMode ? 'text-red-300' : 'text-red-600'
                  }`}>{stats.overconfidence_events}</div>
                  <div className={`text-[10px] uppercase tracking-[0.14em] mt-1.5 font-semibold truncate ${
                    isDarkMode ? 'text-red-400/80' : 'text-red-700/80'
                  }`}>
                    <TermDef
                      label="conf-but-wrong"
                      definition="Mnemo reviews where the student rated their confidence high (≥4/5) before flipping the card, then got the answer wrong. Each event is one self-review."
                    />
                  </div>
                </div>
                {danger.length > 0 && (
                  <>
                    <div className={`hidden sm:block w-px ${isDarkMode ? 'bg-white/[0.08]' : 'bg-gray-200'}`} aria-hidden="true" />
                    <div className="min-w-0">
                      <div className={`text-[20px] sm:text-[22px] font-bold tabular-nums leading-none tracking-tight ${
                        isDarkMode ? 'text-red-300' : 'text-red-600'
                      }`}>{danger.length}</div>
                      <div className={`text-[10px] uppercase tracking-[0.14em] mt-1.5 font-semibold truncate ${
                        isDarkMode ? 'text-red-400/80' : 'text-red-700/80'
                      }`}>
                        <TermDef
                          label="danger zone"
                          definition="Concepts where the student is systematically overconfident. Must have ≥2 reps in the last 4 weeks and a recency-weighted bias ≥0.20 (above the population baseline for human overconfidence)."
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {total > 0 && (
            <div>
              {/* True 2×2 quadrant with labeled axes + central crosshair.
                  Spatial position carries meaning: x = confidence,
                  y = outcome. The danger cell (top-right) is positioned so
                  the eye lands on it first when scanning a 2×2 in English
                  reading order, reinforcing pre-attentive urgency.
                  On mobile (<sm) the axes hide and the cells stack 2x2. */}
              <div className="relative">
                {/* X-axis labels (top) — only visible on sm+ */}
                <div
                  className="hidden sm:grid mb-2.5"
                  style={{ gridTemplateColumns: '64px 1fr 1fr' }}
                  aria-hidden="true"
                >
                  <div />
                  <div className={`text-center text-[10px] font-bold uppercase tracking-[0.16em] ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    Unsure
                  </div>
                  <div className={`text-center text-[10px] font-bold uppercase tracking-[0.16em] ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    Confident
                  </div>
                </div>

                {/* Body row — Y-axis label column appears only on sm+; on
                    mobile the 2×2 grid uses full width without axes. */}
                <div
                  className="grid grid-cols-[1fr] sm:grid-cols-[64px_1fr_1fr]"
                >
                  <div className="hidden sm:flex flex-col" aria-hidden="true">
                    <div className={`flex-1 flex items-center justify-end pr-3.5 text-[10px] font-bold uppercase tracking-[0.16em] ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      Wrong
                    </div>
                    <div className={`flex-1 flex items-center justify-end pr-3.5 text-[10px] font-bold uppercase tracking-[0.16em] ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      Correct
                    </div>
                  </div>

                  {/* The 2×2 — relative parent for the central crosshair */}
                  <div className="col-span-1 sm:col-span-2 relative">
                      {/* Central crosshair lines — subtle, mark the (low/high)×(wrong/right) divide */}
                      <div
                        aria-hidden="true"
                        className={`hidden sm:block absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-px ${
                          isDarkMode ? 'bg-white/[0.05]' : 'bg-black/[0.06]'
                        }`}
                      />
                      <div
                        aria-hidden="true"
                        className={`hidden sm:block absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px ${
                          isDarkMode ? 'bg-white/[0.05]' : 'bg-black/[0.06]'
                        }`}
                      />

                      <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                        {/* Top-left: Unsure & Wrong */}
                        <QuadrantCell
                          count={uw} total={total}
                          label="Unsure & Wrong"
                          sub="expected miss"
                          variant="neutral-warn"
                          selected={selected === 'unsure_wrong'}
                          onClick={() => setSelected('unsure_wrong')}
                        />
                        {/* Top-right: Confident & Wrong (the danger cell) */}
                        <QuadrantCell
                          count={cw} total={total}
                          label="Confident & Wrong"
                          sub="overconfidence"
                          variant="danger"
                          icon={<ShieldExclamationIcon className="w-4 h-4" />}
                          selected={selected === 'confident_wrong'}
                          onClick={() => setSelected('confident_wrong')}
                        />
                        {/* Bottom-left: Unsure & Correct */}
                        <QuadrantCell
                          count={uc} total={total}
                          label="Unsure & Correct"
                          sub="lucky"
                          variant="neutral-good"
                          icon={<QuestionMarkCircleIcon className="w-4 h-4" />}
                          selected={selected === 'unsure_correct'}
                          onClick={() => setSelected('unsure_correct')}
                        />
                        {/* Bottom-right: Confident & Correct */}
                        <QuadrantCell
                          count={cc} total={total}
                          label="Confident & Correct"
                          sub="mastered"
                          variant="good"
                          icon={<CheckBadgeIcon className="w-4 h-4" />}
                          selected={selected === 'confident_correct'}
                          onClick={() => setSelected('confident_correct')}
                        />
                      </div>
                    </div>
                  </div>
                </div>

              {/* Drawer (re-mounted on cell change via key) */}
              <QuadrantDrawer
                key={selected}
                selectedKey={selected}
                count={countByKey[selected]}
                dangerConcepts={danger}
              />
            </div>
          )}

          {total === 0 && (
            <EmptyHint
              icon={<ScaleIcon className="w-7 h-7" />}
              title="No quadrant data this week"
              hint="The pipeline didn't compute a calibration quadrant — likely too few Mnemo events."
            />
          )}
        </>
      )}
    </Section>
  );
};

// ─── M7: Common Question Themes ────────────────────────────────────────────

const reviewStatusConfig = (status: string, isDark: boolean) => {
  if (status === 'still_struggling') return {
    color: isDark ? 'text-red-300' : 'text-red-700',
    bg: isDark ? 'bg-red-500/15' : 'bg-red-50',
    dot: 'bg-red-400', label: 'Still struggling',
  };
  if (status === 'mastered') return {
    color: isDark ? 'text-emerald-300' : 'text-emerald-700',
    bg: isDark ? 'bg-emerald-500/15' : 'bg-emerald-50',
    dot: 'bg-emerald-400', label: 'Mastered',
  };
  return {
    color: isDark ? 'text-yellow-300' : 'text-yellow-700',
    bg: isDark ? 'bg-yellow-500/15' : 'bg-yellow-50',
    dot: 'bg-yellow-400', label: 'To verify',
  };
};

const ThemesCard: React.FC<{
  themes: StudentTheme[];
  study_pattern: string[];
  question_evolution_overall: string;
}> = ({ themes, study_pattern, question_evolution_overall }) => {
  const { isDarkMode } = useTheme();
  const hasThemes = themes.length > 0;

  return (
    <Section
      eyebrow="Conversation starters"
      title="Question Themes"
      icon={<ChatBubbleLeftRightIcon className="w-5 h-5" />}
      tint="cool"
      stagger={0.24}
    >
      {!hasThemes && study_pattern.length === 0 && !question_evolution_overall ? (
        <EmptyHint
          icon={<ChatBubbleLeftRightIcon className="w-7 h-7" />}
          title="Quiet week in chat"
          hint="No recurring questions surfaced. Themes need ≥2 messages clustered around the same concept."
        />
      ) : (
        <>
          {hasThemes && (
            <div className="space-y-2.5 mb-5">
              {themes.map((t, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className={`rounded-xl border p-4 sm:p-[18px] ${
                    isDarkMode ? 'bg-navy-700/35 border-white/[0.06]' : 'bg-gray-50/70 border-gray-200/80'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
                    <h4 className={`text-[15px] font-semibold tracking-tight ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {t.label}
                    </h4>
                    <span className={`text-[11px] tabular-nums font-medium flex items-center gap-2 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      {t.n_questions} {t.n_questions === 1 ? 'question' : 'questions'}
                      {t.is_new && (
                        <span className={`text-[9px] px-1.5 py-[2px] rounded font-bold uppercase tracking-[0.14em] ${
                          isDarkMode ? 'bg-frost-500/15 text-frost-300 ring-1 ring-frost-400/25' : 'bg-frost-50 text-frost-700 ring-1 ring-frost-200'
                        }`}>NEW</span>
                      )}
                    </span>
                  </div>

                  {t.evolution_narrative && (
                    <p className={`text-[13px] mb-3 leading-[1.55] ${
                      isDarkMode ? 'text-gray-300/90' : 'text-gray-600'
                    }`}>
                      {t.evolution_narrative}
                    </p>
                  )}

                  {t.related_concepts.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {t.related_concepts.map((c, j) => {
                        const cfg = reviewStatusConfig(c.review_status, isDarkMode);
                        return (
                          <span key={j} title={cfg.label}
                            className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-[3px] rounded-full ${cfg.bg} ${cfg.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} aria-hidden="true" />
                            {c.concept}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {t.sample_questions.length > 0 && (
                    <details className="group">
                      <summary className={`text-[11px] font-semibold uppercase tracking-[0.14em] cursor-pointer inline-flex items-center gap-1 transition-colors focus:outline-none focus-visible:text-frost-400 ${
                        isDarkMode ? 'text-gray-500 hover:text-frost-300' : 'text-gray-400 hover:text-frost-600'
                      }`}>
                        Sample questions
                        <ChevronDownIcon className="w-3 h-3 transition-transform group-open:rotate-180" aria-hidden="true" />
                      </summary>
                      <div className="mt-3 space-y-2">
                        {t.sample_questions.map((s, k) => (
                          <p key={k} className={`text-[13px] italic pl-3 border-l-2 leading-[1.5] ${
                            isDarkMode ? 'text-gray-400 border-frost-500/30' : 'text-gray-600 border-frost-300'
                          }`}>
                            "{s.text}"
                          </p>
                        ))}
                      </div>
                    </details>
                  )}
                </motion.div>
              ))}
            </div>
          )}

          {study_pattern.length > 0 && (
            <div className="mb-5">
              <p className={`text-[10px] font-semibold uppercase tracking-[0.16em] mb-2.5 ${
                isDarkMode ? 'text-gray-500' : 'text-gray-400'
              }`}>Study Pattern</p>
              <ul className="space-y-2">
                {study_pattern.map((s, i) => (
                  <li key={i} className={`text-[13px] leading-[1.55] flex items-start gap-2.5 ${
                    isDarkMode ? 'text-gray-300/90' : 'text-gray-600'
                  }`}>
                    <span className={`mt-[7px] w-1 h-1 rounded-full flex-shrink-0 ${
                      isDarkMode ? 'bg-frost-400' : 'bg-frost-500'
                    }`} aria-hidden="true" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {question_evolution_overall && (
            <div>
              <div className="flex items-center gap-2 mb-2.5">
                <p className={`text-[10px] font-semibold uppercase tracking-[0.16em] ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`}>How Questions Evolved</p>
                <span className={`text-[9px] px-1.5 py-[1px] rounded font-semibold uppercase tracking-wider ${
                  isDarkMode ? 'bg-violet-500/15 text-violet-300 ring-1 ring-violet-400/20' : 'bg-violet-50 text-violet-700 ring-1 ring-violet-200'
                }`}>AI</span>
              </div>
              <p className={`text-[13px] leading-[1.6] ${isDarkMode ? 'text-gray-300/90' : 'text-gray-600'}`}>
                {question_evolution_overall}
              </p>
            </div>
          )}
        </>
      )}
    </Section>
  );
};

// ─── M8: At Risk of Forgetting ─────────────────────────────────────────────

// Per-concept row inside an expanded chapter group. Tight, secondary
// hierarchy — chapter context is already implied by the parent.
const AtRiskConceptLine: React.FC<{
  c: AtRiskConceptItem;
  index: number;
}> = ({ c, index }) => {
  const { isDarkMode } = useTheme();
  const isCritical = c.tier === 'critical';

  const dot = isCritical
    ? isDarkMode ? 'bg-red-400' : 'bg-red-500'
    : isDarkMode ? 'bg-amber-400' : 'bg-amber-500';

  const daysColor = isCritical
    ? isDarkMode ? 'text-red-300' : 'text-red-600'
    : isDarkMode ? 'text-amber-300' : 'text-amber-700';

  const outcomeChip = (a: string | null) => {
    if (!a) return null;
    const cfg =
      a === 'forgot'
        ? { bg: isDarkMode ? 'bg-red-500/12' : 'bg-red-50', tx: isDarkMode ? 'text-red-300' : 'text-red-700' }
        : a === 'fuzzy'
          ? { bg: isDarkMode ? 'bg-amber-500/12' : 'bg-amber-50', tx: isDarkMode ? 'text-amber-300' : 'text-amber-700' }
          : { bg: isDarkMode ? 'bg-emerald-500/12' : 'bg-emerald-50', tx: isDarkMode ? 'text-emerald-300' : 'text-emerald-700' };
    return (
      <span className={`text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-[2px] rounded ${cfg.bg} ${cfg.tx}`}>
        {a.replace('_', ' ')}
      </span>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: Math.min(index * 0.018, 0.2), duration: 0.3 }}
      className={`flex items-center gap-2.5 px-4 py-2 transition-colors ${
        isDarkMode ? 'hover:bg-white/[0.02]' : 'hover:bg-black/[0.02]'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`} aria-hidden="true" />
      <span className={`text-[13px] font-medium tracking-tight truncate flex-1 min-w-0 ${
        isDarkMode ? 'text-gray-100' : 'text-gray-800'
      }`}>
        {c.concept}
      </span>
      {outcomeChip(c.last_actual)}
      <span className={`text-[12px] font-semibold tabular-nums whitespace-nowrap ${daysColor}`}>
        {c.days_overdue}d
      </span>
    </motion.div>
  );
};

// ─── Chapter rollup ────────────────────────────────────────────────────────
// The tutor's planning unit is the chapter ("we'll review Electromagnetic
// Waves this session"), not individual concepts. Roll up by chapter, sort by
// critical-count then total-count, surface 3 sample concept names per chapter.
// Click to expand → see every concept in that chapter.

type ChapterRollup = {
  key: string;
  chapter_number: number | null;
  chapter_name: string | null;
  concepts: AtRiskConceptItem[];
  n_critical: number;
  n_overdue: number;
  max_days: number;
};

const buildChapterRollups = (concepts: AtRiskConceptItem[]): ChapterRollup[] => {
  const map = new Map<string, ChapterRollup>();
  for (const c of concepts) {
    const key = `${c.chapter_number ?? 'none'}|${c.chapter_name ?? ''}`;
    let r = map.get(key);
    if (!r) {
      r = {
        key,
        chapter_number: c.chapter_number ?? null,
        chapter_name: c.chapter_name ?? null,
        concepts: [],
        n_critical: 0,
        n_overdue: 0,
        max_days: 0,
      };
      map.set(key, r);
    }
    r.concepts.push(c);
    if (c.tier === 'critical') r.n_critical += 1;
    else r.n_overdue += 1;
    if (c.days_overdue > r.max_days) r.max_days = c.days_overdue;
  }
  // Sort each chapter's concept list by days_overdue DESC for sample preview
  const arr = Array.from(map.values());
  arr.forEach((r) => {
    r.concepts.sort((a, b) => b.days_overdue - a.days_overdue);
  });
  return arr.sort((a, b) => {
    if (b.n_critical !== a.n_critical) return b.n_critical - a.n_critical;
    if (b.concepts.length !== a.concepts.length) return b.concepts.length - a.concepts.length;
    return b.max_days - a.max_days;
  });
};

const ChapterRollupCard: React.FC<{
  rollup: ChapterRollup;
  index: number;
  /** Max critical count across all rollups, for the visual urgency bar */
  globalMaxCritical: number;
}> = ({ rollup, index, globalMaxCritical }) => {
  const { isDarkMode } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const total = rollup.concepts.length;
  const sample = rollup.concepts.slice(0, 3).map((c) => c.concept);
  const dominantTier = rollup.n_critical >= rollup.n_overdue ? 'critical' : 'overdue';

  const accent = dominantTier === 'critical'
    ? {
        bg: isDarkMode ? 'bg-red-500/[0.04]' : 'bg-red-50/40',
        border: isDarkMode ? 'border-red-500/15' : 'border-red-200/80',
        borderHover: isDarkMode ? 'hover:border-red-500/25' : 'hover:border-red-300',
        chapNum: isDarkMode ? 'text-red-300' : 'text-red-700',
        chapNumBg: isDarkMode ? 'bg-red-500/12 ring-1 ring-red-400/25' : 'bg-red-100 ring-1 ring-red-200',
      }
    : {
        bg: isDarkMode ? 'bg-amber-500/[0.03]' : 'bg-amber-50/40',
        border: isDarkMode ? 'border-amber-500/15' : 'border-amber-200/80',
        borderHover: isDarkMode ? 'hover:border-amber-500/25' : 'hover:border-amber-300',
        chapNum: isDarkMode ? 'text-amber-300' : 'text-amber-700',
        chapNumBg: isDarkMode ? 'bg-amber-500/12 ring-1 ring-amber-400/25' : 'bg-amber-100 ring-1 ring-amber-200',
      };

  // Bar = critical share relative to the worst chapter on the page (so bars
  // tell a comparative story instead of always maxing out)
  const barPct = globalMaxCritical > 0
    ? Math.max(8, Math.round((rollup.n_critical / globalMaxCritical) * 100))
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.035, 0.28), duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className={`rounded-xl border transition-colors ${accent.bg} ${accent.border} ${accent.borderHover} overflow-hidden`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={`w-full text-left px-3 sm:px-4 py-3.5 flex items-center gap-3 sm:gap-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-frost-400/50 focus-visible:ring-inset ${
          isDarkMode ? 'hover:bg-white/[0.02]' : 'hover:bg-black/[0.02]'
        }`}
        aria-expanded={expanded}
      >
        {/* Chapter number badge — strong leftmost anchor */}
        <span
          className={`flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center text-[14px] sm:text-[15px] font-bold tabular-nums tracking-tight ${accent.chapNumBg} ${accent.chapNum}`}
          aria-hidden="true"
        >
          {rollup.chapter_number ?? '—'}
        </span>

        {/* Chapter name + sample concept preview */}
        <div className="min-w-0 flex-1">
          <h4 className={`text-[13px] sm:text-[14px] font-semibold tracking-tight truncate leading-snug ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {rollup.chapter_name ?? 'Uncategorized concepts'}
          </h4>
          <p className={`text-[11px] mt-1 truncate ${
            isDarkMode ? 'text-gray-500' : 'text-gray-500'
          }`}>
            {sample.join(' · ')}
            {total > sample.length && (
              <span className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>
                {' '}+ {total - sample.length} more
              </span>
            )}
          </p>
        </div>

        {/* Stat block: critical / overdue counts */}
        <div className="flex-shrink-0 flex items-center gap-2.5 sm:gap-3.5">
          {rollup.n_critical > 0 && (
            <div className="text-right">
              <div className={`text-[17px] font-bold tabular-nums leading-none tracking-tight ${
                isDarkMode ? 'text-red-300' : 'text-red-600'
              }`}>
                {rollup.n_critical}
              </div>
              <div className={`text-[9px] uppercase tracking-[0.14em] mt-1 font-semibold ${
                isDarkMode ? 'text-red-400/70' : 'text-red-500/80'
              }`}>
                critical
              </div>
            </div>
          )}
          {rollup.n_overdue > 0 && (
            <div className="text-right">
              <div className={`text-[17px] font-bold tabular-nums leading-none tracking-tight ${
                isDarkMode ? 'text-amber-300' : 'text-amber-700'
              }`}>
                {rollup.n_overdue}
              </div>
              <div className={`text-[9px] uppercase tracking-[0.14em] mt-1 font-semibold ${
                isDarkMode ? 'text-amber-400/70' : 'text-amber-600/80'
              }`}>
                overdue
              </div>
            </div>
          )}
        </div>

        <ChevronDownIcon
          className={`flex-shrink-0 w-4 h-4 transition-transform duration-200 ${
            expanded ? 'rotate-180' : ''
          } ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}
          aria-hidden="true"
        />
      </button>

      {/* Comparative urgency bar — bottom edge of header */}
      <div className={`relative h-[2px] ${isDarkMode ? 'bg-white/[0.03]' : 'bg-black/[0.03]'}`} aria-hidden="true">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${barPct}%` }}
          transition={{ duration: 0.55, delay: 0.18 + Math.min(index * 0.035, 0.28), ease: [0.22, 1, 0.36, 1] }}
          className={`absolute inset-y-0 left-0 ${
            dominantTier === 'critical'
              ? 'bg-gradient-to-r from-red-500/70 to-red-400/30'
              : 'bg-gradient-to-r from-amber-500/70 to-amber-400/30'
          }`}
        />
      </div>

      {/* Expanded concept list */}
      {expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          className={`overflow-hidden border-t ${
            isDarkMode ? 'border-white/[0.04]' : 'border-black/[0.04]'
          }`}
        >
          <div className={`py-1.5 ${
            isDarkMode ? 'bg-navy-900/40' : 'bg-white/50'
          }`}>
            {rollup.concepts.map((c, k) => (
              <AtRiskConceptLine key={`${c.concept}-${k}`} c={c} index={k} />
            ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

const AT_RISK_DEFAULT_CHAPTERS = 5;

const AtRiskCard: React.FC<{
  data: { stats?: any; concepts?: AtRiskConceptItem[] };
}> = ({ data }) => {
  const { isDarkMode } = useTheme();
  const concepts = data?.concepts || [];
  const n_overdue = data?.stats?.n_overdue || 0;
  const n_critical = data?.stats?.n_critical || 0;
  const [showAll, setShowAll] = useState(false);

  const rollups = useMemo(() => buildChapterRollups(concepts), [concepts]);
  const visibleRollups = showAll ? rollups : rollups.slice(0, AT_RISK_DEFAULT_CHAPTERS);
  const hiddenChapters = rollups.length - visibleRollups.length;
  const maxCritical = rollups.length > 0
    ? Math.max(1, ...rollups.map((r) => r.n_critical))
    : 1;

  return (
    <Section
      eyebrow="Spaced-repetition decay"
      title="At Risk of Forgetting"
      icon={<ExclamationTriangleIcon className="w-5 h-5" />}
      tint="warm"
      stagger={0.32}
      trailing={
        n_overdue > 0 ? (
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className={`text-3xl font-bold tabular-nums leading-none tracking-tight ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {n_overdue}
              </div>
              <div className={`text-[10px] uppercase tracking-wider mt-1.5 font-medium ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <TermDef
                  label="overdue"
                  definition="Concepts past their next-review due date set by the spaced-repetition algorithm. Each Mnemo review pushes the next-review date forward; missing a review window means memory has likely started to decay."
                />
              </div>
            </div>
            {n_critical > 0 && (
              <>
                <div className={`h-10 w-px ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`} />
                <div className="text-right">
                  <div className={`text-3xl font-bold tabular-nums leading-none tracking-tight ${
                    isDarkMode ? 'text-red-300' : 'text-red-600'
                  }`}>
                    {n_critical}
                  </div>
                  <div className={`text-[10px] uppercase tracking-wider mt-1.5 font-medium ${
                    isDarkMode ? 'text-red-400/70' : 'text-red-500/80'
                  }`}>
                    <TermDef
                      label="critical"
                      definition="Concepts ≥5 days past their due date. Memory likely lost without review."
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        ) : undefined
      }
    >
      {n_overdue === 0 ? (
        <EmptyHint
          icon={<LightBulbIcon className="w-7 h-7" />}
          title="Review schedule on track"
          hint="No concepts past their due date. The student is keeping up with spaced-repetition reviews."
        />
      ) : (
        <>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Grouped by chapter — the unit a tutor plans around. Click a chapter to see every concept inside it.
          </p>

          {/* Chapter-rollup tiles — the tutor's planning unit */}
          <div className="space-y-2.5">
            {visibleRollups.map((r, i) => (
              <ChapterRollupCard
                key={r.key}
                rollup={r}
                index={i}
                globalMaxCritical={maxCritical}
              />
            ))}
          </div>

          {hiddenChapters > 0 && !showAll && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className={`mt-4 text-xs font-medium flex items-center gap-1 transition-colors ${
                isDarkMode
                  ? 'text-frost-400 hover:text-frost-300'
                  : 'text-frost-600 hover:text-frost-700'
              }`}
            >
              Show all {rollups.length} chapters with overdue concepts
              <ChevronDownIcon className="w-3.5 h-3.5" />
            </button>
          )}
          {showAll && rollups.length > AT_RISK_DEFAULT_CHAPTERS && (
            <button
              type="button"
              onClick={() => setShowAll(false)}
              className={`mt-4 text-xs font-medium flex items-center gap-1 transition-colors ${
                isDarkMode
                  ? 'text-gray-400 hover:text-gray-300'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Collapse to top {AT_RISK_DEFAULT_CHAPTERS}
              <ChevronDownIcon className="w-3.5 h-3.5 rotate-180" />
            </button>
          )}
        </>
      )}
    </Section>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────

const AnalyticsStudentDetailLive: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { instituteId, groupId, bookId, userId } = useParams<{
    instituteId: string; groupId: string; bookId: string; userId: string;
  }>();

  const [selectedWeekStart, setSelectedWeekStart] = useState<string | null>(null);
  const { data, loading, error } = useAnalyticsStudent(groupId, bookId, userId, selectedWeekStart);

  const wss = data?.weekly_summary;
  const m9_bullets = useMemo<AiSummaryBullet[]>(() => {
    const aiSum = wss?.ai_summary as any;
    return Array.isArray(aiSum?.bullets) ? aiSum.bullets : [];
  }, [wss]);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-navy-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* ─── Header — sticks at top ─────────────────────────────────── */}
      <div
        className={`sticky top-0 z-30 border-b ${
          isDarkMode
            ? 'bg-navy-900/85 border-white/[0.07] backdrop-blur-xl backdrop-saturate-150'
            : 'bg-white/85 border-gray-200/70 backdrop-blur-xl backdrop-saturate-150'
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-8 lg:px-12 py-3.5 sm:py-4">
          <button
            onClick={() => navigate(`/analytics-live/${instituteId}/group/${groupId}/book/${bookId}`)}
            className={`group inline-flex items-center gap-1.5 text-[11px] sm:text-xs mb-2 font-medium uppercase tracking-[0.14em] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-frost-400/60 rounded ${
              isDarkMode ? 'text-gray-500 hover:text-frost-300' : 'text-gray-500 hover:text-frost-700'
            }`}
          >
            <ArrowLeftIcon className="w-3 h-3 transition-transform group-hover:-translate-x-0.5" />
            Cohort
          </button>

          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className={`text-xl sm:text-[26px] font-semibold tracking-[-0.01em] leading-none ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {data?.student.full_name || data?.student.email || 'Student'}
                </h1>
                <LiveBadge />
              </div>
              <p className={`text-xs sm:text-[13px] mt-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {data?.cohort_header.group_name || '—'}
                {data?.cohort_header.book_title && <> · {data.cohort_header.book_title}</>}
                {data?.selected_week_start && <> · week of {data.selected_week_start}</>}
              </p>
            </div>

            <div className="relative">
              <select
                className={`appearance-none pl-8 pr-7 py-1.5 rounded-lg text-xs sm:text-sm font-medium cursor-pointer outline-none transition-all focus-visible:ring-2 focus-visible:ring-frost-400/60 ${
                  isDarkMode
                    ? 'bg-navy-800/70 border border-white/10 text-gray-200 hover:border-white/20 hover:bg-navy-800'
                    : 'bg-white/80 border border-gray-200 text-gray-900 hover:border-gray-300 hover:bg-white'
                }`}
                value={data?.selected_week_start || ''}
                onChange={(e) => setSelectedWeekStart(e.target.value || null)}
              >
                {(data?.available_week_starts || []).map((w) => (
                  <option key={w} value={w}>Week of {w}</option>
                ))}
                {!(data?.available_week_starts || []).length && <option value="">No data yet</option>}
              </select>
              <CalendarDaysIcon className="w-3.5 h-3.5 text-frost-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <ChevronDownIcon className={`w-3 h-3 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`} />
            </div>
          </div>
        </div>
      </div>

      {/* ─── Body — single column, generous rhythm ─────────────────── */}
      <div className="max-w-7xl mx-auto px-3 sm:px-8 lg:px-12 py-7 sm:py-10 space-y-6 sm:space-y-8">
        {error && (
          <div className={`rounded-xl border px-4 py-3 text-sm flex items-center gap-2 ${
            isDarkMode ? 'border-red-500/30 bg-red-500/10 text-red-300' : 'border-red-200 bg-red-50 text-red-700'
          }`}>
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {loading && !data && (
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Loading student deep-dive…
          </div>
        )}

        {data && wss && (
          <>
            <AiSummaryHeader bullets={m9_bullets} />

            <ReadingCard
              reading_breakdown={wss.reading_breakdown || {}}
              total_minutes={wss.total_minutes_in_week || 0}
              active_days={wss.active_days_in_week || 0}
              week_start={data.selected_week_start}
              chapter_map={data.cohort_header.book_chapters}
            />

            <CalibrationCard data={(wss.overconfidence_concepts as any) || {}} />

            <ThemesCard
              themes={(wss.student_question_themes as any)?.themes || []}
              study_pattern={(wss.student_question_themes as any)?.study_pattern || []}
              question_evolution_overall={
                (wss.student_question_themes as any)?.question_evolution_overall || ''
              }
            />

            <AtRiskCard data={(wss.at_risk_concepts as any) || {}} />
          </>
        )}

        {data && !wss && (
          <EmptyHint
            icon={<CalendarDaysIcon className="w-8 h-8" />}
            title="No data for this week"
            hint="Pick another week from the picker above, or wait for the next pipeline run."
          />
        )}
      </div>
    </div>
  );
};

export default AnalyticsStudentDetailLive;
